import React, { useRef, useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { Layers, Upload, Loader2 } from 'lucide-react';
import { api, apiUpload } from '../../lib/api';
import { errorMessage } from '../../hooks/useApi';
import type { StudioEpisode, StudioTitle } from './TitleCatalog';

function videoState(ep: StudioEpisode): { label: string; variant: 'default' | 'success' | 'warning' | 'danger' } {
  if (!ep.bunnyVideoId) return { label: 'No video yet', variant: 'default' };
  if (ep.transcodeStatus === 'failed') return { label: 'Failed', variant: 'danger' };
  if (ep.transcodeStatus === 'ready') return { label: 'Ready', variant: 'success' };
  return { label: 'Processing', variant: 'warning' };
}

const duration = (s: number) =>
  s > 0 ? `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}` : '—';

const numInput =
  'w-full px-2.5 py-1.5 bg-[#100b1a] border border-[#2c2140] rounded-lg text-xs text-white';

export function EpisodeManagerModal({
  open,
  onOpenChange,
  title,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: StudioTitle | null;
  /** Called after any change so the parent reloads fresh data. */
  onChanged: () => void;
}) {
  const [seasonNumber, setSeasonNumber] = useState<number | null>(null);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [count, setCount] = useState(10);
  const [freeCount, setFreeCount] = useState(3);
  const [coinPrice, setCoinPrice] = useState(5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<{ id: string; message: string } | null>(null);
  const targetId = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!title) return null;

  const seasons = title.seasons;
  const current = seasons.find((s) => s.seasonNumber === seasonNumber) ?? seasons[0];
  // The API returns rows in no particular order; people expect episode 1 first.
  const episodes = [...(current?.episodes ?? [])].sort((x, y) => x.episodeNumber - y.episodeNumber);

  const handleBulk = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsGenerating(true);
    setBulkError(null);
    try {
      await api(`/admin/titles/${title.id}/episodes/bulk`, {
        method: 'POST',
        body: { count, freeCount, coinPrice },
      });
      setIsBulkOpen(false);
      onChanged();
    } catch (err) {
      setBulkError(errorMessage(err));
    } finally {
      setIsGenerating(false);
    }
  };

  const pickFile = (id: string) => {
    targetId.current = id;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const id = targetId.current;
    if (!file || !id) return;
    setUploadingId(id);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      await apiUpload(`/admin/episodes/${id}/upload`, form);
      onChanged();
    } catch (err) {
      setUploadError({ id, message: errorMessage(err) });
    } finally {
      setUploadingId(null);
      targetId.current = null;
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Episodes: ${title.title}`}
      description="Add episode slots, then upload a video for each one."
      maxWidth="4xl"
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFile}
        accept="video/mp4,video/quicktime,video/webm,video/x-matroska"
        className="hidden"
      />

      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-[#241b33]">
          <div className="flex items-center gap-1.5">
            {seasons.length > 1 &&
              seasons.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSeasonNumber(s.seasonNumber)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    s.id === current?.id
                      ? 'bg-violet-600 text-white shadow-sm'
                      : 'bg-[#171022] border border-[#2c2140] text-gray-400 hover:text-white'
                  }`}
                >
                  {s.title || `Season ${s.seasonNumber}`} ({s.episodes.length})
                </button>
              ))}
          </div>
          <button
            onClick={() => setIsBulkOpen(!isBulkOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1a1228] hover:bg-[#251a3a] border border-[#352554] text-violet-300 text-xs font-medium transition-colors"
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Add episodes</span>
          </button>
        </div>

        {isBulkOpen && (
          <form onSubmit={handleBulk} className="p-4 rounded-xl bg-[#181126] border border-violet-500/30 space-y-3">
            <p className="text-xs text-gray-400">
              This creates empty episode slots. You upload the videos afterwards.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-300 mb-1">How many episodes</label>
                <input type="number" min={1} max={150} value={count} onChange={(e) => setCount(Number(e.target.value))} className={numInput} />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-300 mb-1">How many are free</label>
                <input type="number" min={0} max={50} value={freeCount} onChange={(e) => setFreeCount(Number(e.target.value))} className={numInput} />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-300 mb-1">Coins per locked episode</label>
                <input type="number" min={1} max={50} value={coinPrice} onChange={(e) => setCoinPrice(Number(e.target.value))} className={numInput} />
              </div>
            </div>
            {bulkError && (
              <p className="text-xs text-rose-400" role="alert">
                {bulkError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setIsBulkOpen(false)} className="px-3 py-1 text-xs text-gray-400 hover:text-white">
                Cancel
              </button>
              <button
                type="submit"
                disabled={isGenerating}
                className="px-3.5 py-1 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold disabled:opacity-50"
              >
                {isGenerating ? 'Adding…' : `Add ${count} episodes`}
              </button>
            </div>
          </form>
        )}

        <div className="border border-[#241b33] rounded-xl overflow-hidden bg-[#0d0914]">
          <div className="max-h-[50vh] overflow-auto">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-[#160f22] text-gray-400 font-semibold uppercase tracking-wider text-[10px] border-b border-[#241b33]">
                <tr>
                  <th className="px-4 py-2.5">Ep</th>
                  <th className="px-4 py-2.5">Title</th>
                  <th className="px-4 py-2.5">Length</th>
                  <th className="px-4 py-2.5">Access</th>
                  <th className="px-4 py-2.5">Video</th>
                  <th className="px-4 py-2.5 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1e152d] text-gray-300">
                {episodes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No episodes yet. Use "Add episodes" above to create them.
                    </td>
                  </tr>
                ) : (
                  episodes.map((ep) => {
                    const uploading = uploadingId === ep.id;
                    const st = videoState(ep);
                    const err = uploadError?.id === ep.id ? uploadError.message : null;
                    return (
                      <tr key={ep.id}>
                        <td className="px-4 py-2.5 font-bold text-white">#{ep.episodeNumber}</td>
                        <td className="px-4 py-2.5 truncate max-w-[180px]">{ep.title || `Episode ${ep.episodeNumber}`}</td>
                        <td className="px-4 py-2.5 text-gray-400">{duration(ep.durationSeconds)}</td>
                        <td className="px-4 py-2.5">
                          {ep.isFree ? <Badge variant="success">Free</Badge> : <Badge variant="warning">{ep.coinPrice} coins</Badge>}
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant={st.variant}>{st.label}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <button
                            onClick={() => pickFile(ep.id)}
                            disabled={uploadingId !== null}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#171022] hover:bg-violet-600 hover:text-white border border-[#2c2140] text-gray-300 transition-colors text-[11px] font-medium disabled:opacity-50"
                          >
                            {uploading ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin text-violet-400" />
                                <span>Uploading…</span>
                              </>
                            ) : (
                              <>
                                <Upload className="w-3 h-3" />
                                <span>{ep.bunnyVideoId ? 'Replace video' : 'Upload video'}</span>
                              </>
                            )}
                          </button>
                          {err && <p className="text-[11px] text-rose-400 mt-1 max-w-[220px] ml-auto">{err}</p>}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
}
